import { useInfiniteQuery } from '@tanstack/react-query';
import api from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

interface PollSummary {
  pollId: string;
  title: string;
  status: string;
  resultsVisibility: string;
  closesAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface UsePollsOptions {
  status?: string;
}

interface UsePollsReturn {
  polls: PollSummary[];
  total: number;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  loadMore: () => void;
  hasMore: boolean;
}

const LIMIT = 20;

export function usePolls(options: UsePollsOptions = {}): UsePollsReturn {
  const { data, isLoading, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: queryKeys.polls.list({ status: options.status }),
      queryFn: async ({ pageParam = 0 }) => {
        const result = await api.listMyPolls({
          status: options.status || undefined,
          limit: LIMIT,
          offset: pageParam,
        });
        return result;
      },
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) => {
        const next = allPages.length * LIMIT;
        return next < lastPage.total ? next : undefined;
      },
    });

  const polls = data?.pages.flatMap((p) => p.polls) ?? [];
  const total = data?.pages[0]?.total ?? 0;

  return {
    polls,
    total,
    loading: isLoading,
    error: error instanceof Error ? error.message : error ? 'Failed to load polls' : null,
    refresh: () => {
      refetch();
    },
    loadMore: () => {
      if (hasNextPage && !isFetchingNextPage) fetchNextPage();
    },
    hasMore: !!hasNextPage,
  };
}
